import React, { useMemo } from "react";
import { Badge, Box, Tooltip } from "@mui/material";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import { useQuery } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import { useAssetNodeForMachine } from "../../../hooks/useAssetNodeForMachine";
import { useActiveToolServiceStatus } from "../../../hooks/useActiveToolServiceStatus";
import { getWorkOrders } from "../../../fetchers/cmmsWorkOrder";
import cmmsMessages from "../../cmms/cmmsMessages";
import {
  ToolServiceStatusLevel,
  buildToolServiceSummary,
  getToolServiceLevelColor,
  isToolServiceAlerting,
} from "../../cmms/toolServiceStatus";
import { isWorkOrderOpen } from "../../cmmsWorkOrders/workOrderEnums";
import workOrderMessages from "../../cmmsWorkOrders/messages";

/*
 * The maintenance tab's label.
 *
 * Carries two unrelated facts, so it carries two marks. The number is open work orders, the
 * same thing the number on the Activities and Alarms tabs means. The wrench is the tool in the
 * machine needing service, and appears only at Warning or Overdue. Collapsing them into one
 * coloured number was rejected: a red "3" reads as "3 urgent work orders", which is not what it
 * would mean.
 *
 * This is the ambient half of the story and therefore reads on every machine page load, unlike
 * the tab body which only mounts when opened. The cost is kept to what it has to be: the work
 * order query uses the identical key the body uses, so opening the tab costs nothing extra, and
 * the hierarchy tree is already cached by the report-problem button beside it.
 *
 * Both marks must survive the other being absent — a tool alert with no open work orders shows
 * the wrench alone, and open work orders with a healthy tool show the number alone.
 */

export default function MachineWorkOrdersTabLabel({ machineId, companyKey }) {
  const intl = useIntl();

  const { assetNode } = useAssetNodeForMachine(machineId, companyKey);
  const assetNodeId = assetNode?.id ?? null;

  // Same key and same filters as MachineWorkOrdersTab, deliberately: react-query then serves
  // both from one request, and the count can never disagree with the list it counts.
  const queryFilters = useMemo(() => ({ assetNodeId }), [assetNodeId]);

  const workOrdersQuery = useQuery(
    ["cmmsWorkOrders", companyKey, queryFilters],
    () => getWorkOrders(companyKey, queryFilters),
    { enabled: Boolean(companyKey && assetNodeId), staleTime: 30000 },
  );

  const { tool, status } = useActiveToolServiceStatus(assetNodeId);

  // isWorkOrderOpen rather than a local list: the definition is shared so this badge,
  // the sidebar's and the work order page's priority banner can never drift apart.
  const openCount = useMemo(
    () => (workOrdersQuery.data ?? []).filter((wo) => isWorkOrderOpen(wo.status)).length,
    [workOrdersQuery.data],
  );

  const label = intl.formatMessage(workOrderMessages.machineTabLabel);
  const alerting = isToolServiceAlerting(status?.level);

  if (!alerting) {
    // badgeContent={0} renders nothing by default, so a machine with no open work orders and a
    // healthy tool gets a plain label with no empty circle.
    return <Badge badgeContent={openCount} color="warning">{label}</Badge>;
  }

  const sentence = intl.formatMessage(
    status.level === ToolServiceStatusLevel.Overdue
      ? cmmsMessages.toolStatusBadgeOverdue
      : cmmsMessages.toolStatusBadgeWarning,
    { tool: tool?.toolName ?? status.toolId },
  );

  const tooltip = [sentence, status.scheduleTitle, buildToolServiceSummary(status, intl)]
    .filter(Boolean)
    .join(" · ");

  return (
    <Badge badgeContent={openCount} color="warning">
      <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
        {label}
        <Tooltip title={tooltip} arrow>
          {/* role="img" so the sentence is actually announced — aria-label on a bare span has
              no role to attach to and is skipped by most screen readers. Colour alone must not
              be the only carrier of "this is a warning". */}
          <Box
            component="span"
            role="img"
            aria-label={sentence}
            sx={{ display: "inline-flex", color: getToolServiceLevelColor(status.level) }}
          >
            <BuildCircleIcon fontSize="small" />
          </Box>
        </Tooltip>
      </Box>
    </Badge>
  );
}
